/**
 * v6.1.17.7 N.2 — Username picker modal.
 *
 * Triggered globally by the `fws:username-required` window event that the
 * axios response interceptor in `src/lib/api.js` fires when ANY backend
 * call returns 403 `USERNAME_NOT_SET` (from `RequireUsernameSetMiddleware`).
 *
 * Also opens explicitly from the Profile page when the user is flagged
 * `username_legacy: true` and clicks the "Update username" button.
 *
 * No-dismiss while open: the user MUST set a username before any
 * write API call succeeds.  Background overlay blocks all clicks;
 * Escape / outside-click intercepted.
 *
 * On success: PATCH /users/me/username, refresh the auth context, and
 * reload the page so any in-flight failed requests retry naturally.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

// Mirror of `backend/identity.py::USERNAME_PATTERN`.
// Tests: `frontend/src/__tests__/UsernamePickerModal.test.js`.
const USERNAME_REGEX = /^[a-zA-Z0-9_]{3,24}$/;
const USERNAME_MIN = 3;
const USERNAME_MAX = 24;

function validateClientSide(value) {
  if (!value) return 'Pick a username.';
  if (value.length < USERNAME_MIN) return `At least ${USERNAME_MIN} characters.`;
  if (value.length > USERNAME_MAX) return `At most ${USERNAME_MAX} characters.`;
  if (!USERNAME_REGEX.test(value)) return 'Only letters, numbers, and underscores.';
  return null;
}

export function UsernamePickerModal({ open, onClose, reason = null, allowDismiss = false }) {
  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    if (!open) {
      setValue('');
      setSubmitting(false);
      setServerError(null);
    }
  }, [open]);

  const clientError = value === '' ? null : validateClientSide(value);
  const canSubmit = !submitting && !clientError && value !== '';

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError(null);
    try {
      await api.patch('/users/me/username', { username: value });
      toast.success('Username set — welcome!');
      // Reload to retry any in-flight failed requests + refresh auth context.
      if (allowDismiss && onClose) {
        onClose(true);
      } else if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = detail?.code;
      if (code === 'USERNAME_TAKEN') {
        setServerError('Already taken — try another.');
      } else if (code === 'USERNAME_INVALID') {
        setServerError(detail?.message || 'Invalid username.');
      } else if (code === 'USERNAME_REJECTED_MODERATION') {
        setServerError('Pick a different username.');
      } else if (code === 'USERNAME_FROZEN') {
        setServerError('Your username is already set. Contact support if you need an exception.');
        // eslint-disable-next-line no-console
        console.warn('[username-picker] received USERNAME_FROZEN — middleware/state desync?', detail);
      } else {
        setServerError(detail?.message || 'Could not save — try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Block dismissal unless explicitly allowed (legacy-banner flow can dismiss).
        if (!next && allowDismiss && onClose) onClose(false);
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        // No-dismiss: block Escape + outside-click when allowDismiss is false.
        onEscapeKeyDown={(e) => { if (!allowDismiss) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (!allowDismiss) e.preventDefault(); }}
        onInteractOutside={(e) => { if (!allowDismiss) e.preventDefault(); }}
        data-testid="username-picker-modal"
      >
        <DialogHeader>
          <DialogTitle>Pick your username</DialogTitle>
          <DialogDescription>
            This is your public identifier across the game.  It's frozen after you set it,
            so pick one you like.
          </DialogDescription>
        </DialogHeader>

        {reason?.username_legacy && (
          <div
            className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
            data-testid="username-picker-legacy-reason"
          >
            Your existing username doesn't match our new format.  Please pick a new one.
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="username-input">Username</Label>
          <Input
            id="username-input"
            data-testid="username-picker-input"
            placeholder="your_username"
            value={value}
            onChange={(e) => { setValue(e.target.value); setServerError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) submit(); }}
            autoFocus
            maxLength={USERNAME_MAX}
            disabled={submitting}
            aria-invalid={!!(clientError || serverError)}
            aria-describedby="username-input-help"
          />
          <p id="username-input-help" className="text-xs text-muted-foreground">
            3-24 characters · letters, numbers, underscores
          </p>
          {clientError && (
            <p className="text-xs text-destructive" data-testid="username-picker-client-error">
              {clientError}
            </p>
          )}
          {serverError && (
            <p className="text-xs text-destructive" data-testid="username-picker-server-error">
              {serverError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          {allowDismiss && (
            <Button
              variant="ghost"
              onClick={() => onClose?.(false)}
              disabled={submitting}
              data-testid="username-picker-cancel"
            >Cancel</Button>
          )}
          <Button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="username-picker-submit"
          >
            {submitting ? 'Setting…' : 'Pick username'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Top-level mount that listens to the `fws:username-required` event and
 * shows the modal.  Drop this once near the app root.
 */
export function UsernamePickerHost() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (ev) => {
      setReason(ev?.detail || null);
      setOpen(true);
    };
    window.addEventListener('fws:username-required', handler);
    return () => window.removeEventListener('fws:username-required', handler);
  }, []);

  return (
    <UsernamePickerModal
      open={open}
      onClose={() => setOpen(false)}
      reason={reason}
      allowDismiss={false}
    />
  );
}

export default UsernamePickerHost;
